'use client'

import { useCallback, useEffect, useState } from 'react'
import { Link } from '@/shared/navigation'
import {
  submitDelmanAttendance,
  type SubmitDelmanAttendanceRequest,
} from '@/features/delman/api/submitDelmanAttendance'
import { delmanTerms } from '@/features/delman/delmanTerms'
import {
  getDelmanAttendanceStatus,
  loadDelmanAttendance,
  saveDelmanAttendance,
  todayDateKey,
  type DelmanAttendanceStatus,
  type DelmanDayAttendance,
} from '@/features/delman/storage/delmanAttendanceStorage'
import { blocksDelmanCheckout, type TripRecord } from '@/features/delman/storage/tripStorage'
import { loadTodayTripPreferApi } from '@/features/delman/api/delmanTripApi'
import type { AttendanceAction } from '@/features/kitchen/api/submitAttendance'
import { getDelmanDevice } from '@/shared/auth/delmanDeviceStorage'
import { uploadPhoto } from '@/shared/api/uploadPhoto'
import { PinKeypadModal } from '@/shared/components/PinKeypadModal'
import { SelfieCapture } from '@/shared/components/SelfieCapture'
import { Toast } from '@/shared/components/Toast'
import { consumeLastSyncQueued } from '@/shared/sync/kitchenSync'
import { useClientHydrated } from '@/shared/hooks/useClientHydrated'

const DELMAN_SHIFT = { start: '05:30', end: '14:00' }

type FlowStep = 'home' | 'selfie' | 'confirm'

function formatTodayShort(): string {
  return new Intl.DateTimeFormat('id-ID', {
    timeZone: 'Asia/Jakarta',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date())
}

function formatTimeLabel(iso: string): string {
  return new Intl.DateTimeFormat('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
}

function formatNowClock(): string {
  return new Intl.DateTimeFormat('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date())
}

function formatDuration(checkIn: string, checkOut: string): string {
  const ms = new Date(checkOut).getTime() - new Date(checkIn).getTime()
  const totalMinutes = Math.max(0, Math.floor(ms / 60000))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${hours}j ${minutes}m`
}

function isOnTimeCheckIn(iso: string, shiftStart: string): boolean {
  const event = new Date(iso)
  const start = new Date(event)
  const [hour, minute] = shiftStart.split(':').map(Number)
  start.setHours(hour, minute, 0, 0)
  return event.getTime() <= start.getTime()
}

function statusLabel(status: DelmanAttendanceStatus): string {
  if (status === 'belum') return delmanTerms.attendance.notYet
  if (status === 'masuk') return delmanTerms.attendance.onShift
  return delmanTerms.attendance.finished
}

function statusBadgeClass(status: DelmanAttendanceStatus): string {
  if (status === 'belum') return 'badge-draft'
  if (status === 'masuk') return 'badge-warning'
  return 'badge-success'
}

function revokePhotoUrl(url: string | null) {
  if (url?.startsWith('blob:')) {
    URL.revokeObjectURL(url)
  }
}

function DelmanAttendanceStepper({ current, pinOpen }: { current: 1 | 2 | 3; pinOpen: boolean }) {
  const steps = [
    { n: 1, label: delmanTerms.attendance.stepSelfie },
    { n: 2, label: delmanTerms.attendance.stepConfirm },
    { n: 3, label: delmanTerms.attendance.stepPin },
  ]
  const active = pinOpen ? 3 : current

  return (
    <div className="flex items-center gap-1">
      {steps.map((step, index) => {
        const state = step.n < active ? 'done' : step.n === active ? 'current' : 'upcoming'
        return (
          <div key={step.n} className="flex flex-1 items-center gap-1 min-w-0">
            <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
              <div
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-label-md font-bold ${
                  state === 'done'
                    ? 'bg-status-success text-white'
                    : state === 'current'
                      ? 'bg-primary text-on-primary'
                      : 'bg-surface-container text-on-surface-variant border border-outline-variant'
                }`}
              >
                {state === 'done' ? (
                  <span className="material-symbols-outlined text-base">check</span>
                ) : (
                  step.n
                )}
              </div>
              <span
                className={`text-[10px] font-semibold text-center leading-tight truncate w-full ${
                  state === 'current' ? 'text-primary' : 'text-on-surface-variant'
                }`}
              >
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`h-0.5 flex-1 min-w-2 mb-4 ${
                  steps[index + 1].n <= active ? 'bg-status-success' : 'bg-outline-variant'
                }`}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

export function DelmanAttendancePage() {
  const hydrated = useClientHydrated()
  const device = hydrated ? getDelmanDevice() : null
  const date = todayDateKey()

  const [record, setRecord] = useState<DelmanDayAttendance | null>(null)
  const [step, setStep] = useState<FlowStep>('home')
  const [pendingAction, setPendingAction] = useState<AttendanceAction | null>(null)
  const [selfiePreview, setSelfiePreview] = useState<string | null>(null)
  const [selfieError, setSelfieError] = useState<string | null>(null)
  const [pinOpen, setPinOpen] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [nowClock, setNowClock] = useState(formatNowClock())
  const [todayTrip, setTodayTrip] = useState<TripRecord | null>(null)

  const dismissToast = useCallback(() => setToast(null), [])
  const showToast = useCallback((message: string) => setToast(message), [])

  useEffect(() => {
    if (!hydrated || !device) return
    setRecord(loadDelmanAttendance(device.delmanId, date))
  }, [hydrated, device?.delmanId, date])

  useEffect(() => {
    if (!hydrated || !device) return
    let cancelled = false
    void loadTodayTripPreferApi({
      delmanId: device.delmanId,
      delmanName: device.delmanName,
      kitchenName: device.kitchenName,
    }).then((trip) => {
      if (!cancelled) setTodayTrip(trip)
    })
    return () => {
      cancelled = true
    }
  }, [hydrated, device?.delmanId, device?.delmanName, device?.kitchenName])

  useEffect(() => {
    if (!hydrated || !device) return
    const activeDevice = device
    function refreshTrip() {
      void loadTodayTripPreferApi({
        delmanId: activeDevice.delmanId,
        delmanName: activeDevice.delmanName,
        kitchenName: activeDevice.kitchenName,
      }).then(setTodayTrip)
    }
    window.addEventListener('focus', refreshTrip)
    document.addEventListener('visibilitychange', refreshTrip)
    return () => {
      window.removeEventListener('focus', refreshTrip)
      document.removeEventListener('visibilitychange', refreshTrip)
    }
  }, [hydrated, device?.delmanId, device?.delmanName, device?.kitchenName])

  useEffect(() => {
    const timer = window.setInterval(() => setNowClock(formatNowClock()), 30_000)
    return () => window.clearInterval(timer)
  }, [])

  const status = getDelmanAttendanceStatus(record)
  const tripBlocksCheckout = todayTrip != null && blocksDelmanCheckout(todayTrip)
  const flowStepNumber: 1 | 2 | 3 = step === 'selfie' ? 1 : 2

  const actionLabel =
    pendingAction === 'check_out'
      ? delmanTerms.attendance.checkOutVerb
      : delmanTerms.attendance.checkInVerb
  const actionTitle =
    pendingAction === 'check_out' ? delmanTerms.attendance.checkOut : delmanTerms.attendance.checkIn

  function resetFlow() {
    revokePhotoUrl(selfiePreview)
    setSelfiePreview(null)
    setSelfieError(null)
    setPendingAction(null)
    setPinOpen(false)
    setStep('home')
  }

  function startAction(action: AttendanceAction) {
    if (action === 'check_out' && tripBlocksCheckout) {
      showToast(delmanTerms.attendance.tripNotCompleted)
      return
    }
    setPendingAction(action)
    setSelfieError(null)
    setSelfiePreview(null)
    setStep('selfie')
  }

  function handlePhotoChange(url: string | null) {
    if (selfiePreview && url !== selfiePreview) {
      revokePhotoUrl(selfiePreview)
    }
    setSelfiePreview(url)
    setSelfieError(null)
  }

  function openConfirmStep() {
    if (!selfiePreview) {
      setSelfieError(delmanTerms.attendance.selfieRequired)
      return
    }
    setStep('confirm')
  }

  async function confirmAttendance(pin: string) {
    if (!device || !pendingAction || !selfiePreview) {
      throw new Error(delmanTerms.attendance.incompleteData)
    }

    const clientEventId = crypto.randomUUID()
    const uploaded = await uploadPhoto(selfiePreview, 'attendance', { clientEventId })

    const req: SubmitDelmanAttendanceRequest = {
      deviceId: device.deviceId,
      delmanId: device.delmanId,
      pin,
      action: pendingAction,
      photoUrl: uploaded.key,
    }

    const result = await submitDelmanAttendance(req)

    const event = {
      at: result.recorded_at,
      timeLabel: formatTimeLabel(result.recorded_at),
      photoUrl: uploaded.url,
      onTime:
        pendingAction === 'check_in'
          ? (result.on_time ?? isOnTimeCheckIn(result.recorded_at, DELMAN_SHIFT.start))
          : undefined,
    }

    const base: DelmanDayAttendance = record ?? {
      delmanId: device.delmanId,
      delmanName: device.delmanName,
      kitchenName: device.kitchenName,
      date,
      shiftStart: DELMAN_SHIFT.start,
      shiftEnd: DELMAN_SHIFT.end,
    }

    const next: DelmanDayAttendance = {
      ...base,
      checkIn: base.checkIn,
      checkOut: base.checkOut,
    }
    if (pendingAction === 'check_in') next.checkIn = event
    else next.checkOut = event

    saveDelmanAttendance(next)
    setRecord(next)
    setPinOpen(false)

    const verb =
      pendingAction === 'check_in'
        ? delmanTerms.attendance.checkInVerb
        : delmanTerms.attendance.checkOutVerb
    let message =
      pendingAction === 'check_in'
        ? `${device.delmanName} ${verb} ${event.timeLabel}${event.onTime ? ` · ${delmanTerms.attendance.onTime}` : ` · ${delmanTerms.attendance.late}`}`
        : `${device.delmanName} ${verb} ${event.timeLabel}`

    if (consumeLastSyncQueued()) {
      message += ` · ${delmanTerms.sync.queued}`
    }

    resetFlow()
    showToast(message)
  }

  if (!hydrated) {
    return <div className="min-h-[200px]" aria-hidden />
  }

  if (!device) {
    return (
      <section className="card space-y-3">
        <p className="text-body-sm text-status-danger">{delmanTerms.attendance.deviceNotActivated}</p>
        <Link href="/delman/activate" className="btn-outline block text-center">
          {delmanTerms.attendance.activateAction}
        </Link>
      </section>
    )
  }

  return (
    <section className="space-y-3">
      {step === 'home' && (
        <>
          <div className="card text-center space-y-4 py-5">
            <p className="text-headline-md text-primary">{delmanTerms.attendance.pageTitle}</p>
            <div className="w-14 h-14 rounded-full bg-primary-container text-on-primary-container flex items-center justify-center mx-auto">
              <span className="material-symbols-outlined text-3xl">face_retouching_natural</span>
            </div>
            <div>
              <p className="text-body-sm text-on-surface-variant">{delmanTerms.attendance.statusToday}</p>
              <p className="text-headline-md text-primary mt-1">{statusLabel(status)}</p>
            </div>
            <div className="text-body-sm text-on-surface-variant space-y-1">
              <p className="break-words">
                <strong className="text-on-surface">{device.delmanName}</strong> · {device.kitchenName}
              </p>
              <p>
                {delmanTerms.attendance.shift}: {DELMAN_SHIFT.start} – {DELMAN_SHIFT.end} · {formatTodayShort()}
              </p>
            </div>
            <span className={`inline-block ${statusBadgeClass(status)}`}>{statusLabel(status)}</span>

            {status === 'belum' && (
              <button
                type="button"
                className="btn-primary w-full max-w-sm mx-auto min-h-[52px] text-lg btn-tactile"
                onClick={() => startAction('check_in')}
              >
                {delmanTerms.attendance.checkIn}
              </button>
            )}
            {status === 'masuk' && !tripBlocksCheckout && (
              <p className="text-body-sm text-on-surface-variant max-w-sm mx-auto">
                {delmanTerms.attendance.stayOnShiftHint}
              </p>
            )}
            {status === 'masuk' && tripBlocksCheckout && (
              <Link
                href="/delman/trip"
                className="block rounded-lg border border-status-warning/40 bg-status-warning-bg/30 px-3 py-3 text-left"
              >
                <div className="flex items-start gap-2">
                  <span className="material-symbols-outlined text-status-warning shrink-0">local_shipping</span>
                  <div className="min-w-0">
                    <p className="text-body-sm font-semibold text-status-warning">
                      {delmanTerms.attendance.tripNotCompleted}
                    </p>
                    <p className="text-body-sm text-on-surface-variant mt-1">
                      {delmanTerms.attendance.tripNotCompletedAction}
                    </p>
                  </div>
                  <span className="material-symbols-outlined text-on-surface-variant shrink-0">chevron_right</span>
                </div>
              </Link>
            )}
            {status === 'masuk' && (
              <button
                type="button"
                className="btn-outline w-full max-w-sm mx-auto min-h-[52px] border-2 border-primary text-primary font-bold disabled:opacity-50"
                onClick={() => startAction('check_out')}
                disabled={tripBlocksCheckout}
              >
                {delmanTerms.attendance.checkOut} · {delmanTerms.attendance.checkoutButtonSuffix}
              </button>
            )}

            <p className="text-body-sm text-on-surface-variant">{delmanTerms.attendance.selfiePinRequired}</p>
            <p className="text-[11px] text-on-surface-variant">{delmanTerms.attendance.selfOnlyHint}</p>
          </div>

          {record?.checkIn && (
            <div className="card space-y-2 text-body-sm">
              <p>
                <span className="text-on-surface-variant">{delmanTerms.attendance.checkIn}</span>{' '}
                <strong className="font-mono">{record.checkIn.timeLabel}</strong>
                {record.checkIn.onTime === false && (
                  <span className="text-status-warning"> · {delmanTerms.attendance.late}</span>
                )}
                {record.checkIn.onTime && (
                  <span className="text-status-success"> · {delmanTerms.attendance.onTime}</span>
                )}
              </p>
              {record.checkOut && (
                <>
                  <p>
                    <span className="text-on-surface-variant">{delmanTerms.attendance.checkOut}</span>{' '}
                    <strong className="font-mono">{record.checkOut.timeLabel}</strong>
                  </p>
                  <p className="text-on-surface-variant">
                    {delmanTerms.attendance.duration(
                      formatDuration(record.checkIn.at, record.checkOut.at),
                    )}
                  </p>
                </>
              )}
            </div>
          )}

          <p className="text-body-sm text-on-surface-variant text-center">{delmanTerms.attendance.offlineNote}</p>
        </>
      )}

      {step !== 'home' && pendingAction && (
        <div className="card space-y-3">
          <DelmanAttendanceStepper current={flowStepNumber} pinOpen={pinOpen} />
          <div className="flex items-center justify-between gap-2 border-b border-surface-variant pb-3">
            <div>
              <p className="text-headline-md text-primary">{actionTitle}</p>
              <p className="text-body-sm text-on-surface-variant">{device.delmanName}</p>
            </div>
            <button type="button" className="btn-outline min-h-[44px]" onClick={resetFlow}>
              {delmanTerms.attendance.cancel}
            </button>
          </div>
        </div>
      )}

      {step === 'selfie' && pendingAction && (
        <div className="card space-y-3">
          <p className="text-body-sm font-semibold">{delmanTerms.attendance.selfieTitle(actionTitle)}</p>
          <SelfieCapture
            photoUrl={selfiePreview}
            onPhotoChange={handlePhotoChange}
            staffName={device.delmanName}
          />
          {selfieError && (
            <div role="alert" className="alert-error">
              <span className="material-symbols-outlined text-base shrink-0">error</span>
              <span>{selfieError}</span>
            </div>
          )}
          <button
            type="button"
            className="btn-primary w-full min-h-[44px] disabled:opacity-50"
            disabled={!selfiePreview}
            onClick={openConfirmStep}
          >
            {delmanTerms.attendance.continueConfirm}
          </button>
        </div>
      )}

      {step === 'confirm' && pendingAction && selfiePreview && (
        <div className="card space-y-3">
          <p className="text-body-sm text-on-surface-variant text-center">{delmanTerms.attendance.confirmHint}</p>
          <div className="rounded-xl border border-outline-variant overflow-hidden">
            <img
              src={selfiePreview}
              alt={delmanTerms.attendance.confirmPhotoAlt(device.delmanName)}
              className="w-full aspect-[4/3] object-cover"
            />
          </div>
          <div className="rounded-lg bg-surface-container p-3 space-y-2 text-body-sm">
            <div className="flex justify-between gap-2">
              <span className="text-on-surface-variant">{delmanTerms.attendance.fieldDelman}</span>
              <strong className="break-words text-right">{device.delmanName}</strong>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-on-surface-variant">{delmanTerms.attendance.fieldKitchen}</span>
              <span className="break-words text-right">{device.kitchenName}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-on-surface-variant">{delmanTerms.attendance.fieldAction}</span>
              <strong className="capitalize">{actionLabel}</strong>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-on-surface-variant">{delmanTerms.attendance.fieldTime}</span>
              <strong className="font-mono">{nowClock}</strong>
            </div>
          </div>
          <button type="button" className="btn-primary w-full min-h-[44px]" onClick={() => setPinOpen(true)}>
            {delmanTerms.attendance.continuePin}
          </button>
          <button type="button" className="btn-outline w-full min-h-[44px]" onClick={() => setStep('selfie')}>
            {delmanTerms.attendance.retakeSelfie}
          </button>
        </div>
      )}

      <PinKeypadModal
        open={pinOpen}
        title={delmanTerms.attendance.pinTitle(actionLabel)}
        description={delmanTerms.attendance.pinDescription(device.delmanName, actionLabel)}
        confirmLabel={delmanTerms.attendance.submit}
        onClose={() => setPinOpen(false)}
        onConfirm={confirmAttendance}
      />

      {toast && <Toast message={toast} onDismiss={dismissToast} />}
    </section>
  )
}
